import AddCircleIcon from "@mui/icons-material/AddCircle";
import { Box, Button, Grid, IconButton, MenuItem, TextField, Typography, FormLabel } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import BasicDialog from "../../../components/base/BasicDialog";
import { useCreateProductTagMutation, useProductTags } from "../../../services/productsService";
import { ProductPricing } from "./ProductPricing";
import { FormProvider, useForm, Controller } from "react-hook-form";
import { LoadingButton } from "@mui/lab";

import { useToasts } from "../../../hooks/useToasts";
import toast from "react-hot-toast";

import { useUpdatePricingMutation } from "../../../services/productsService";

import { SnackbarSuccess } from "../../../components/base/SnackbarAlert/SnackbarSuccess";

const productPricingFieldNames = {
    footage: "footage",
    pricing: {
        maxDiscount: "pricing.maxDiscount",
        vat: "pricing.vat",
        listedNetPerSqm: "pricing.listedNetPerSqm",
        minNetPerSqm: "pricing.minNetPerSqm",
        baseNetPerSqm: "pricing.baseNetPerSqm",
        pricingPublished: "pricing.pricingPublished",
    },
};

const formLabelSx = { textAlign: "left", fontWeight: "bold", verticalAlign: "middle", marginRight: "20px" };

export const ProductPricingEditDialog = (props) => {
    const { pricingData } = props;
    const { t } = useTranslation();

    const {
        control,
        setValue,
        watch,
        } = useForm({
            defaultValues: {
                    footage: pricingData.pricing_product_footage || 0.0,
                    footageFinal: null,
                    pricing: {
                        vat:
                            typeof pricingData?.pricing_vat_proc === "string"
                                ? Number.parseFloat(pricingData.pricing_vat_proc) || 0.0
                                : pricingData?.pricing_vat_proc || 0.0,
                        maxDiscount:
                            typeof pricingData?.pricing_discount_max === "string"
                                ? Number.parseFloat(pricingData.pricing_discount_max) || 0.0
                                : pricingData?.pricing_discount_max || 0.0,
                        listedNetPerSqm: Number.parseFloat(pricingData?.pricing_listed_m2_net) || 0.0,
                        minNetPerSqm: Number.parseFloat(pricingData?.pricing_min_m2_net) || 0.0,
                        baseNetPerSqm: Number.parseFloat(pricingData?.pricing_base_m2_net) || 0.0,
                        pricingPublished: pricingData?.pricing_published || false,
                    },
                }
        })

    const { onServerError } = useToasts();

    const { mutate: updatePricing, isPending: isLoading } = useUpdatePricingMutation({
        onSuccess: () => {
            toast.custom(
                () => <SnackbarSuccess message={t("successfullyEdited")} />,
                {
                    position: "bottom-right",
                },
            );
            props.onClose()
        },
        onError: (error) => {
            onServerError(error);
        },
    });    

    const onSubmit = () => {
        updatePricing({id: pricingData.id, data:{
            pricing_product_footage: watch(productPricingFieldNames.footage) || 0,
            pricing_vat_proc: watch(productPricingFieldNames.pricing.vat) || 0,
            pricing_discount_max: watch(productPricingFieldNames.pricing.maxDiscount) || 0,
            pricing_listed_m2_net: watch(productPricingFieldNames.pricing.listedNetPerSqm) || 0,
            pricing_min_m2_net: watch(productPricingFieldNames.pricing.minNetPerSqm) || 0,
            pricing_base_m2_net: watch(productPricingFieldNames.pricing.baseNetPerSqm) || 0,
            pricing_published: watch(productPricingFieldNames.pricing.pricingPublished),
        }})
    };

    return (
        <BasicDialog
            open={true}
            onClose={props.onClose}
            titleAlign={"center"}
            contentAlign={"center"}
            title={t("products.ProductConfigurationDialog.editPricing")}
            maxWidth={"lg"}
            showDialogActions
            alwaysFullScreen={false}
            contentSx={{ pt: 0 }}
        >             
            <ProductPricing
                isMultipleProducts={false}
                readOnly={false}
                control={control}
                fieldNames={productPricingFieldNames}
                setValue={setValue}
                productId={null}
                showProductKind={false}
            />
            <Grid container rowGap={3}>
                <Grid item xs={4}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: "1rem",
                            flex: 1,
                        }}
                    >
                        <Typography>{t("products.ProductParameters.footage")}</Typography>
                        <Controller
                        name={productPricingFieldNames.footage}
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                id="footage"
                                type="number"
                                disabled={false}
                                inputProps={{
                                    min: 0,
                                    step: "any",
                                }}
                            />
                        )}
                    />  
                    </Box>
                </Grid>
                <Grid item xs={4}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: "1rem",
                            flex: 1,
                        }}
                    >
                        <Typography>{t("products.ProductPricing.pricingPublished")}</Typography>
                        <Controller
                            name={productPricingFieldNames.pricing.pricingPublished}
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    value={field.value}
                                    select
                                    fullWidth
                                >
                                    <MenuItem key={true} value={true}>
                                        {t("yes")}
                                    </MenuItem>
                                    <MenuItem key={false} value={false}>
                                        {t("no")}
                                    </MenuItem>
                                </TextField>
                            )}
                        />
                    </Box>
                </Grid>
                    <Grid item xs={12} textAlign={"right"}>
                        <LoadingButton
                            style={{ minWidth: "200px" }}
                            variant="contained"
                            size="small"
                            onClick={onSubmit}
                            color={"success"}
                            fullWidth
                            loading={isLoading}
                        >
                            {t("save")}
                        </LoadingButton>
                    </Grid>
            </Grid> 

        </BasicDialog>
    );
};